import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { BookingProvider } from './context/BookingContext';
import { Toaster } from '@/components/ui/sonner';
import PageLoader from '@/components/layout/PageLoader';
import './App.css';

const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const STARTUP_SPLASH_MS = 3000;
const STARTUP_SPLASH_EXIT_MS = 700;
const STARTUP_LOGO_URL = '/logo.png';

function ScrollToTopOnPathChange() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
}

function LegacyAdminRedirect() {
  const location = useLocation();
  const legacyPath = location.pathname.replace(/^\/admin/, '') || '/';

  return <Navigate to={`${legacyPath}${location.search}${location.hash}`} replace />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader label="Loading..." />}>
      <Routes>
        <Route path="/admin/*" element={<LegacyAdminRedirect />} />
        <Route path="/*" element={<AdminDashboard />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  const [splashPhase, setSplashPhase] = useState('show');

  useEffect(() => {
    const startExitTimer = window.setTimeout(() => {
      setSplashPhase('exit');
    }, STARTUP_SPLASH_MS);

    const removeSplashTimer = window.setTimeout(() => {
      setSplashPhase('hidden');
    }, STARTUP_SPLASH_MS + STARTUP_SPLASH_EXIT_MS);

    return () => {
      window.clearTimeout(startExitTimer);
      window.clearTimeout(removeSplashTimer);
    };
  }, []);

  const splashVisible = splashPhase !== 'hidden';
  const splashIsExiting = splashPhase === 'exit';

  return (
    <ThemeProvider>
      <BookingProvider>
        {splashVisible && (
          <div
            className={`startup-splash${splashIsExiting ? ' startup-splash--exit' : ''}`}
            aria-hidden="true"
          >
            <div className="startup-splash__vignette" />
            <div className="startup-splash__logo-glow" />
            <img
              src={STARTUP_LOGO_URL}
              alt="Bijou's Barbershop"
              className="startup-splash__logo"
              decoding="async"
            />
          </div>
        )}

        <div className={`app-shell${splashVisible ? ' app-shell--hidden' : ''}`}>
          <Router>
            <ScrollToTopOnPathChange />
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </Router>
        </div>
      </BookingProvider>
    </ThemeProvider>
  );
}

export default App;
